import type { CSSProperties } from 'react';
import { useT } from '../../i18n/index.ts';
import { cx } from '../../ui/index.ts';
import { DECK_COLORS } from '../../ui/tokens.ts';
import { retiredRegions } from './legPhysical.ts';
import { routingShares, type EffectiveRouting, type RoutingShare } from './legRouting.ts';
import type { PlacedLeg } from './legSkus.ts';
import { routingShortLabel } from './legText.ts';

/**
 * 首发流量分配 as a two-column table (#2658): routing name (+ customers) on
 * the left, that routing's own segmented bar on the right. Each routing
 * draws separately, so each bar is normalised inside itself. Segments are
 * one signal orange (identity is the label, as on the tenant routing pool
 * bar); a leg that gave its first pick up — an armed trip in a region, or
 * its targets in this routing disabled — is striped and named under the bar.
 */

const RETIRED_STYLE: CSSProperties = {
  backgroundImage: `repeating-linear-gradient(135deg, ${DECK_COLORS.bad} 0 3px, ${DECK_COLORS['bad-dim']} 3px 6px)`,
};

/** A segment this wide carries its own label; narrower ones are named in the legend under the bar. */
const INSIDE_MIN_PERCENT = 18;

/** Legend label: the upstream name, widened by channel / model only where two segments collide. */
function segmentLabels(legs: readonly PlacedLeg[]): ReadonlyMap<string, string> {
  const nameCount = new Map<string, number>();
  for (const placed of legs) nameCount.set(placed.leg.head.upstream_name, (nameCount.get(placed.leg.head.upstream_name) ?? 0) + 1);
  const models = new Set(legs.map((placed) => placed.leg.head.physical_model));
  return new Map(legs.map((placed) => {
    const { head } = placed.leg;
    if ((nameCount.get(head.upstream_name) ?? 0) < 2) return [placed.leg.key, head.upstream_name];
    const parts = [head.upstream_name, head.upstream_channel_id, ...(models.size > 1 ? [head.physical_model] : [])];
    return [placed.leg.key, parts.join(' · ')];
  }));
}

interface Segment {
  readonly placed: PlacedLeg;
  readonly share: RoutingShare;
  readonly label: string;
  /** Region tags (JP / CN) or the disabled word; empty while it still takes first picks. */
  readonly retired: string;
}

function RoutingBar({ routing, legsByKey, t }: {
  readonly routing: EffectiveRouting;
  readonly legsByKey: ReadonlyMap<string, PlacedLeg>;
  readonly t: ReturnType<typeof useT>;
}): JSX.Element {
  const picked = routingShares(routing)
    .filter((share) => share.role === 'primary' && (share.percent ?? 0) > 0)
    .flatMap((share) => {
      const placed = legsByKey.get(share.legKey);
      return placed === undefined ? [] : [{ placed, share }];
    });
  const labels = segmentLabels(picked.map((entry) => entry.placed));
  const segments: readonly Segment[] = picked.map(({ placed, share }) => {
    const regions = retiredRegions(placed.leg).map((region) => region.toUpperCase());
    const retired = share.disabled || placed.leg.state === 'manual'
      ? t('guaranteed.legs.share.disabled')
      : regions.length === 0 ? '' : t('guaranteed.legs.share.retired', { regions: regions.join(' / ') });
    return { placed, share, label: labels.get(placed.leg.key) ?? '', retired };
  });
  // Weight 0 legs that took every first pick in a region (all primaries gone there).
  const takeovers = [...legsByKey.values()].flatMap((placed) => {
    const role = placed.roles.find((entry) => entry.routing.key === routing.key);
    return role === undefined || role.takeover.length === 0 ? [] : [{ placed, regions: role.takeover }];
  });
  const takeoverLegend = takeovers.length === 0 ? null : (
    <ul className="m-0 flex list-none flex-wrap gap-x-3.5 gap-y-0.5 p-0 text-micro">
      {takeovers.map(({ placed, regions }) => (
        <li
          key={placed.leg.key}
          className="inline-flex items-baseline gap-1.5 text-warn"
          data-testid={`guaranteed-leg-share-takeover-${routing.key}`}
        >
          <span aria-hidden="true" className="h-1.5 w-1.5 self-center rounded-full bg-warn" />
          <span className="font-mono">{placed.leg.head.upstream_name}</span>
          <span>{t('guaranteed.legs.takeover', { regions: regions.map((region) => region.toUpperCase()).join(' / ') })}</span>
        </li>
      ))}
    </ul>
  );
  const inside = (segment: Segment): boolean =>
    segment.retired.length === 0 && (segment.share.percent ?? 0) >= INSIDE_MIN_PERCENT;
  const legend = segments.filter((segment) => !inside(segment));
  if (segments.length === 0) {
    return (
      <div className="flex min-w-0 flex-col gap-1">
        <span className="text-label text-paper-low">{t('guaranteed.legs.share.none')}</span>
        {takeoverLegend}
      </div>
    );
  }
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex h-5 gap-px overflow-hidden rounded bg-line-soft">
        {segments.map((segment) => {
          const text = `${segment.label} ${String(segment.share.percent ?? 0)}%`;
          return (
            <div
              key={segment.placed.leg.key}
              className={cx(
                'flex h-full flex-none items-center overflow-hidden px-1.5',
                segment.retired.length === 0 && 'bg-signal',
              )}
              style={{
                width: `${String(segment.share.percent ?? 0)}%`,
                ...(segment.retired.length === 0 ? {} : RETIRED_STYLE),
              }}
              title={segment.retired.length === 0 ? text : `${text} · ${segment.retired}`}
              data-retired={segment.retired.length > 0 ? 'true' : undefined}
            >
              {inside(segment) ? (
                <span
                  className="truncate font-mono text-micro text-signal-ink"
                  data-testid={`guaranteed-leg-share-item-${routing.key}`}
                >
                  {text}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
      {legend.length === 0 ? null : (
        <ul className="m-0 flex list-none flex-wrap gap-x-3.5 gap-y-0.5 p-0 text-micro">
          {legend.map((segment) => (
            <li
              key={segment.placed.leg.key}
              className="inline-flex items-baseline gap-1.5"
              data-testid={`guaranteed-leg-share-item-${routing.key}`}
            >
              <span
                aria-hidden="true"
                className={cx('h-1.5 w-1.5 self-center rounded-full', segment.retired.length === 0 ? 'bg-signal' : 'bg-bad')}
              />
              <span className="font-mono text-paper-mid">{segment.label}</span>
              <span className="font-mono text-paper">{`${String(segment.share.percent ?? 0)}%`}</span>
              {segment.retired.length === 0 ? null : <span className="text-bad">{segment.retired}</span>}
            </li>
          ))}
        </ul>
      )}
      {takeoverLegend}
    </div>
  );
}

export function LegShareBars({ routings, legs }: {
  readonly routings: readonly EffectiveRouting[];
  readonly legs: readonly PlacedLeg[];
}): JSX.Element {
  const t = useT();
  const legsByKey = new Map(legs.map((placed) => [placed.leg.key, placed]));
  const override = routings.some((routing) => routing.kind === 'override');
  const globals = routings.filter((routing) => routing.kind === 'global').length;
  return (
    <div className="flex flex-col gap-3 border-b border-line-soft px-4 py-3.5" data-testid="guaranteed-leg-share">
      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
        <span className="text-label text-paper-mid">{t('guaranteed.legs.share.title')}</span>
        <span className="text-micro text-paper-low">
          {override ? t('guaranteed.legs.share.override_hint') : t('guaranteed.legs.share.hint')}
        </span>
      </div>
      {routings.length === 0 ? (
        <span className="text-label text-paper-low">{t('guaranteed.legs.share.no_routing')}</span>
      ) : (
        <div className="grid grid-cols-[168px_minmax(0,1fr)] items-start gap-x-4 gap-y-2.5">
          {routings.map((routing) => (
            <div key={routing.key} className="contents" data-testid={`guaranteed-leg-share-${routing.key}`}>
              <div className="flex min-w-0 flex-col pt-0.5">
                <span
                  className="truncate text-label text-paper"
                  title={routingShortLabel(routing, globals, t)}
                  data-testid={`guaranteed-leg-share-label-${routing.key}`}
                >
                  {routingShortLabel(routing, globals, t)}
                </span>
                {routing.kind === 'org' ? (
                  <span className="text-micro text-paper-low">
                    {t('guaranteed.legs.share.orgs', { count: String(routing.assignedOrgs ?? 0) })}
                  </span>
                ) : null}
              </div>
              <RoutingBar routing={routing} legsByKey={legsByKey} t={t} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
